import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { stageRig } from '../lighting/stage-light-rig.js';

// Shared-rig contract (see the framing addendum): this visualizer's
// declared relationship to the one shared StageLightRig instance, and the
// beam intensity it asks for. Most restrictive wins across owners.
const RIG = 'required';
const RIG_OWNER = 'daftpyramid';
const RIG_INTENSITY_SCALE = 1;
const RIG_ZONES = 'default';
// Same lit-floor treatment as neon-pulse.js/kinetic-rig.js: relaxes the rig's
// spot falloff (decay 2.4 -> 1.1) so beams actually reach the ground and draw
// pools on it instead of arriving divided by ~170.
const RIG_FLOOR_LIGHT_BOOST = 3;
// This visualizer had no floor at all, so the rig's beams faded into black
// below the content. 0x16161c is as dark as a black stage floor reads while
// still being able to catch light: much below it, the sRGB->linear decode
// leaves an albedo too small for any spotlight to register on.
const FLOOR_SIZE = 60;
const FLOOR_COLOR = 0x16161c;
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'daftpyramid',
  family: 'stage',
  energyLevel: 0.65,
  bpmMin: 90,
  bpmMax: 175,
  movement: 0.75,
  danceability: 0.80,
  tonality: 0.30,
  enabled: true
};

// DaftPyramid: a wall of neon triangle-lattice trusswork - the central
// pyramid flanked by two wing panels - reproducing the Daft Punk Alive
// 2007 tour stage silhouette. Color is deliberately fixed to that
// specific ice-blue/white neon rather than album-driven like most other
// visualizers here: the brief was to reproduce a specific, recognizable
// look from a reference photo, not a generic "neon" mode.
//
// Built from one shared triangular point lattice (standard offset-row
// construction - alternate rows shifted by half a cell so each rhombus
// splits into two triangles), then only kept where a full unit triangle
// (all 3 vertices) falls inside one of the three silhouette polygons
// (pyramid, left wing, right wing) - see collectCompleteTriangleEdges.
// This is what gives the jagged, modular-truss-like boundary instead of
// a perfectly smooth triangle outline (which if anything reads MORE like
// the real modular truss structure in the photo), while guaranteeing
// every kept edge belongs to a fully closed triangle - never a stray
// fragment left over from a triangle that got partially clipped.
//
// Topology (which points connect to which) never changes after init, so
// LineSegmentsGeometry is built once; only per-segment brightness
// changes every frame, via vertex colors - see _updateColors.

const SPACING = 1.4; // world units between lattice points - was 0.5, then 0.9 ("plus grosses, moins de pyramides") - bigger cells, fewer of them
const COVERAGE = 0.92;
// Matches neon-pulse.js's own camera.position.set(0, 2.6, 7) exactly on
// Z - the pyramid/wing/lattice geometry below is all authored relative
// to W/H, which are themselves derived from CAMERA_DISTANCE, so this
// stays fully self-consistent (the whole composition just rescales to
// still fill the same COVERAGE fraction of frame) rather than changing
// how big the structure reads on screen - the only thing this actually
// changes is the rig's apparent scale/distance, to match how it reads
// in NeonPulse ("rig position points should be the same as NeonPulse").
const CAMERA_DISTANCE = 7;
const GROUND_Y = 0.15; // small gap between the structure's base and the very bottom of frame
const LINE_WIDTH_PX = 3.5; // was 2.2 - "augmente l'epaisseur des neons partout"
const FRAME_WIDTH_PX = 5; // was 3.2 - the top bar's own steady, slightly-thicker-than-the-lattice anchor look, scaled up to match

// "Neon lightnings" on the beat (see beat-pulse.js) - right on the beat,
// EVERY segment (even ones currently in their own "off" phase, brightness
// 0) gets an additive brightness boost toward LIT_COLOR, so the whole
// lattice flashes together for an instant before decaying back to each
// segment's own independent slow flicker - a real synchronized event
// layered on top of the ambient sparse twinkle, not a replacement for
// it. Unlike disco-squares' rejected uniform-intensity flash (a dense,
// evenly-lit grid where that reads as a mechanical strobe), this
// lattice's segments are already sparse/scattered, and an occasional
// all-at-once flash is exactly what "lightning" should look like -
// sudden and synchronized, not ambient.
const BEAT_FLASH_AMOUNT = 0.6;
const BEAT_FLASH_DECAY = 11;

const DIM_COLOR = new THREE.Color(0x0d3d4a); // cool, barely-lit tube - always visible, this is what draws the silhouette
const LIT_COLOR = new THREE.Color(0x9fe8ff); // bright but not blown-out cyan - was near-pure white (0xeaffff), which combined with a high simultaneous-on fraction and bloom washed the whole structure into a white haze
const TOP_BAR_COLOR_LEFT = new THREE.Color(0xd8f6ff);
const TOP_BAR_COLOR_RIGHT = new THREE.Color(0xff9fd6); // the photo's subtle magenta accent on the right half of the top bar

function pointInConvexPolygon(px, py, poly) {
  let sign = 0;
  for (let i = 0; i < poly.length; i++) {
    const x1 = poly[i][0], y1 = poly[i][1];
    const x2 = poly[(i + 1) % poly.length][0], y2 = poly[(i + 1) % poly.length][1];
    const cross = (x2 - x1) * (py - y1) - (y2 - y1) * (px - x1);
    if (cross !== 0) {
      const s = cross > 0 ? 1 : -1;
      if (sign === 0) sign = s;
      else if (s !== sign) return false;
    }
  }
  return true;
}

// Standard triangular lattice: alternate rows offset by half a cell so
// each row-pair's rhombi split into triangles. Returns the raw point
// grid (not edges) - see collectCompleteTriangleEdges for how edges are
// derived from it.
//
// Each point's two "diagonal down" neighbors are exactly one spacing
// away (an equilateral triangle with the horizontal edge): even rows
// connect down to (row+1,col) and (row+1,col+1); odd rows connect down
// to (row+1,col) and (row+1,col-1) - an earlier version connected to
// col-1/col (even) or col/col+1 (odd) instead, where one of those two
// points is actually 1.5*spacing away, not a real neighbor at all - a
// badly-skewed "not aligned" diagonal, while silently never connecting
// the real neighbor it should have.
function buildTriangularLattice(minX, maxX, minY, maxY, spacing) {
  const rowHeight = spacing * Math.sqrt(3) / 2;
  const rows = Math.ceil((maxY - minY) / rowHeight) + 2;
  const cols = Math.ceil((maxX - minX) / spacing) + 4;
  const startX = minX - spacing;
  const points = [];
  for (let row = 0; row <= rows; row++) {
    points[row] = [];
    const y = minY + row * rowHeight;
    const xOffset = row % 2 === 1 ? spacing / 2 : 0;
    for (let col = 0; col <= cols; col++) {
      points[row][col] = [startX + col * spacing - xOffset, y];
    }
  }
  return { points, rows, cols };
}

// Walks every small triangle in the lattice (two per row-band cell) and
// keeps an edge only if it belongs to at least one triangle whose all 3
// vertices fall inside a silhouette shape - not just "this edge's own 2
// endpoints happen to both be inside something" (which still let a
// single stray edge survive right at a boundary when only 2 of its
// triangle's 3 vertices were actually inside, with the third dropped -
// a real edge, correctly connected on both its own ends, but with no
// complete shape around it - e.g. "one neon over the top edge of the
// structure" poking above the last complete row near the apex). This is
// the strict reading of "every structure should be complete, no line
// not connected on both sides": every kept edge is part of a fully
// closed triangle, never a fragment of one that got clipped away.
function collectCompleteTriangleEdges(points, rows, cols, shapes) {
  const inAnyShape = (p) => shapes.some((poly) => pointInConvexPolygon(p[0], p[1], poly));
  const edgeMap = new Map();
  const addEdge = (a, b) => {
    const key = a[0].toFixed(4) + ',' + a[1].toFixed(4) + '|' + b[0].toFixed(4) + ',' + b[1].toFixed(4);
    const revKey = b[0].toFixed(4) + ',' + b[1].toFixed(4) + '|' + a[0].toFixed(4) + ',' + a[1].toFixed(4);
    if (!edgeMap.has(key) && !edgeMap.has(revKey)) edgeMap.set(key, [a, b]);
  };
  const tryTriangle = (a, b, c) => {
    if (inAnyShape(a) && inAnyShape(b) && inAnyShape(c)) {
      addEdge(a, b);
      addEdge(b, c);
      addEdge(c, a);
    }
  };
  for (let row = 0; row < rows; row++) {
    // dc points toward this row's "extra" diagonal neighbor direction -
    // see buildTriangularLattice's own comment (+1 for even rows, -1
    // for odd rows).
    const dc = row % 2 === 0 ? 1 : -1;
    const colStart = dc === 1 ? 0 : 1;
    const colEnd = dc === 1 ? cols - 1 : cols;
    for (let col = colStart; col <= colEnd; col++) {
      const p0 = points[row][col];
      const p1 = points[row][col + dc];
      const q0 = points[row + 1][col];
      const q1 = points[row + 1][col + dc];
      tryTriangle(p0, p1, q1);
      tryTriangle(p0, q0, q1);
    }
  }
  return Array.from(edgeMap.values());
}

// Where the pyramid's slanted edge (apex -> base corner) crosses a given
// height - used below to close the gap between the pyramid and each
// wing, since the pyramid's apex sits higher than the wing's own top
// edge (so the gap isn't a simple quad between existing corners).
function edgeXAtY(apex, base, y) {
  const t = (apex[1] - y) / (apex[1] - base[1]);
  return apex[0] + (base[0] - apex[0]) * t;
}

export class DaftPyramidVisualizer extends Visualizer {
  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    const vFovRad = THREE.MathUtils.degToRad(camera.fov);
    const halfHeight = CAMERA_DISTANCE * Math.tan(vFovRad / 2);
    const halfWidth = halfHeight * camera.aspect;
    const W = halfWidth * 2 * COVERAGE;
    const H = halfHeight * 2 * COVERAGE;

    // Level view (position.y === lookAt.y), not NeonPulse's own slight
    // downward tilt - a tilt here pushes the tall pyramid/top-bar clean
    // out of frame at the top, since this structure (unlike NeonPulse's
    // free-floating particle scene) already fills the frame vertically
    // edge to edge by design. CAMERA_DISTANCE alone (now 7, matching
    // NeonPulse's own Z) is what makes the rig read at the same scale/
    // distance - see CAMERA_DISTANCE's own comment above.
    //
    // Framed on the structure's own true vertical midpoint (its actual
    // span is [GROUND_Y, H*0.97], not [0, H]) - was H*0.42, noticeably
    // below that midpoint, which pushed the whole structure toward the
    // top of frame with a big empty margin left at the bottom instead of
    // split evenly top/bottom. Nudged down further from that exact
    // midpoint ("remonter un peu la structure verticalement", then
    // "remonte encore un peu") - aiming at a slightly LOWER point makes
    // the (fixed, world-space) structure read as sitting higher on screen.
    const verticalCenter = (GROUND_Y + H * 0.97) / 2 - H * 0.085;
    camera.position.set(0, verticalCenter, CAMERA_DISTANCE);
    camera.lookAt(0, verticalCenter, 0);

    // Same plain StageLightRig setup as neon-pulse.js/neon-particles.js.
    this._rig = stageRig.acquire(RIG_OWNER, {
      scene: scene,
      intensityScale: RIG_INTENSITY_SCALE,
      zones: RIG_ZONES,
      floorLightBoost: RIG_FLOOR_LIGHT_BOOST
    });

    const floorGeometry = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const floorMaterial = new THREE.MeshStandardMaterial({ color: FLOOR_COLOR, roughness: 0.78, metalness: 0.0 });
    this._floor = new THREE.Mesh(floorGeometry, floorMaterial);
    this._floor.rotation.x = -Math.PI / 2;
    scene.add(this._floor);

    // Silhouette polygons in design space (X centered on 0, Y from the
    // ground up) - a central pyramid flanked by two trapezoid wings,
    // matching the reference photo's composition.
    // Apex was H*0.86, leaving a visibly empty triangular gap of black
    // space between the peak and the top bar (H*0.97) above it -
    // extended closer to the bar so the structure reads as reaching the
    // top of frame instead of stopping well short of it.
    const pyramid = [
      [0, H * 0.94],
      [W * 0.13, GROUND_Y],
      [-W * 0.13, GROUND_Y]
    ];
    const leftWing = [
      [-W * 0.49, H * 0.78],
      [-W * 0.1, H * 0.78],
      [-W * 0.16, GROUND_Y],
      [-W * 0.49, GROUND_Y]
    ];
    const rightWing = leftWing.map((p) => [-p[0], p[1]]);

    // Fills the gap between the pyramid and each wing so the lattice
    // reads as one continuous structure instead of three separate
    // modules with a visible black gap between them - "juste une
    // structure complete plutot que deux connectees". The pyramid's
    // apex sits above the wing's own top edge, so this isn't a simple
    // quad between existing corners - its top-inner corner is wherever
    // the pyramid's own slanted edge happens to be at the wing's top
    // height (see edgeXAtY).
    const wingTopY = leftWing[0][1];
    const leftFiller = [
      leftWing[1],
      [edgeXAtY(pyramid[0], pyramid[2], wingTopY), wingTopY],
      pyramid[2],
      leftWing[2]
    ];
    const rightFiller = leftFiller.map((p) => [-p[0], p[1]]);
    const shapes = [pyramid, leftWing, rightWing, leftFiller, rightFiller];

    const { points, rows, cols } = buildTriangularLattice(-W / 2, W / 2, GROUND_Y, H * 0.95, SPACING);
    const keptEdges = collectCompleteTriangleEdges(points, rows, cols, shapes);

    // Top truss bar - two fixed segments (a small gap at the center,
    // like the photo) that stay steadily lit rather than flickering, an
    // anchor the eye reads as "solid structure" against the flickering
    // lattice below.
    const topBarY = H * 0.97;
    this._frameSegments = [
      { a: [-W * 0.49, topBarY], b: [-W * 0.05, topBarY], color: TOP_BAR_COLOR_LEFT },
      { a: [W * 0.05, topBarY], b: [W * 0.49, topBarY], color: TOP_BAR_COLOR_RIGHT }
    ];

    this._count = keptEdges.length;
    const positions = new Float32Array(this._count * 6);
    const colors = new Float32Array(this._count * 6);
    this._onDuration = new Float32Array(this._count);
    this._offDuration = new Float32Array(this._count);
    this._phase = new Float32Array(this._count);

    for (let i = 0; i < this._count; i++) {
      const [a, b] = keptEdges[i];
      positions[i * 6] = a[0];
      positions[i * 6 + 1] = a[1];
      positions[i * 6 + 2] = 0;
      positions[i * 6 + 3] = b[0];
      positions[i * 6 + 4] = b[1];
      positions[i * 6 + 5] = 0;

      // Short, sparse flashes (~6% average duty cycle - was ~12%, still
      // "way too many neons" on screen at once) rather than a near-even
      // on/off split - the first version kept ~35-40% of the ~780
      // segments lit at any moment, which additive-blended and bloomed
      // into a solid white haze instead of readable neon lines.
      const onDuration = 0.2 + Math.random() * 0.45;
      const offDuration = 3.2 + Math.random() * 5.5;
      this._onDuration[i] = onDuration;
      this._offDuration[i] = offDuration;
      this._phase[i] = Math.random() * (onDuration + offDuration);

      colors[i * 6] = DIM_COLOR.r;
      colors[i * 6 + 1] = DIM_COLOR.g;
      colors[i * 6 + 2] = DIM_COLOR.b;
      colors[i * 6 + 3] = DIM_COLOR.r;
      colors[i * 6 + 4] = DIM_COLOR.g;
      colors[i * 6 + 5] = DIM_COLOR.b;
    }

    const geometry = new LineSegmentsGeometry();
    geometry.setPositions(positions);
    geometry.setColors(colors);

    this._material = new LineMaterial({
      linewidth: LINE_WIDTH_PX,
      vertexColors: true,
      transparent: true,
      opacity: 0.95,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    this._material.resolution.set(window.innerWidth, window.innerHeight);

    this._lines = new LineSegments2(geometry, this._material);
    scene.add(this._lines);

    // The InterleavedBuffer backing instanceColorStart/instanceColorEnd -
    // mutated directly every frame instead of calling setColors() again
    // (which would allocate a new buffer every call) - same pattern as
    // streak-layer.js's position updates.
    this._colorBuffer = geometry.attributes.instanceColorStart.data;

    // Separate mesh (own, thicker linewidth) for the steady structural
    // frame (top bar + left/right outer edges) - a solid anchor against
    // the flickering lattice below, never touched again after init since
    // it doesn't animate.
    const framePositions = new Float32Array(this._frameSegments.length * 6);
    const frameColors = new Float32Array(this._frameSegments.length * 6);
    this._frameSegments.forEach((seg, idx) => {
      const base = idx * 6;
      framePositions[base] = seg.a[0];
      framePositions[base + 1] = seg.a[1];
      framePositions[base + 2] = 0;
      framePositions[base + 3] = seg.b[0];
      framePositions[base + 4] = seg.b[1];
      framePositions[base + 5] = 0;
      frameColors[base] = seg.color.r;
      frameColors[base + 1] = seg.color.g;
      frameColors[base + 2] = seg.color.b;
      frameColors[base + 3] = seg.color.r;
      frameColors[base + 4] = seg.color.g;
      frameColors[base + 5] = seg.color.b;
    });
    const frameGeometry = new LineSegmentsGeometry();
    frameGeometry.setPositions(framePositions);
    frameGeometry.setColors(frameColors);
    this._frameMaterial = new LineMaterial({
      linewidth: FRAME_WIDTH_PX,
      vertexColors: true,
      transparent: true,
      opacity: 1,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    this._frameMaterial.resolution.set(window.innerWidth, window.innerHeight);
    this._frame = new LineSegments2(frameGeometry, this._frameMaterial);
    scene.add(this._frame);

    this._time = 0;
  }

  update(deltaTime, profile) {
    stageRig.update(deltaTime, profile, RIG_OWNER);
    this._material.resolution.set(window.innerWidth, window.innerHeight);

    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;

    const intensity = 0.75 + profile.director.intensity * 0.4;
    // 0 normally, spiking toward BEAT_FLASH_AMOUNT right on the beat then
    // decaying fast - added on top of each segment's own brightness
    // below (not multiplied), so it can light up segments that are
    // currently fully off too - see BEAT_FLASH_AMOUNT's comment above.
    const beatFlash = profile.director.pulse(BEAT_FLASH_AMOUNT, BEAT_FLASH_DECAY) - 1;

    const array = this._colorBuffer.array;
    for (let i = 0; i < this._count; i++) {
      const cycle = this._onDuration[i] + this._offDuration[i];
      const t = (this._time + this._phase[i]) % cycle;
      const fadeIn = THREE.MathUtils.smoothstep(t, 0, 0.08);
      const fadeOut = 1 - THREE.MathUtils.smoothstep(t, this._onDuration[i] - 0.08, this._onDuration[i]);
      const brightness = Math.min(1, Math.min(fadeIn, fadeOut) * intensity + beatFlash);

      const r = DIM_COLOR.r + (LIT_COLOR.r - DIM_COLOR.r) * brightness;
      const g = DIM_COLOR.g + (LIT_COLOR.g - DIM_COLOR.g) * brightness;
      const b = DIM_COLOR.b + (LIT_COLOR.b - DIM_COLOR.b) * brightness;

      const base = i * 6;
      array[base] = r;
      array[base + 1] = g;
      array[base + 2] = b;
      array[base + 3] = r;
      array[base + 4] = g;
      array[base + 5] = b;
    }
    this._colorBuffer.needsUpdate = true;
    this._frameMaterial.resolution.set(window.innerWidth, window.innerHeight);
  }

  getLightRig() {
    return this._rig;
  }

  dispose() {
    stageRig.release(RIG_OWNER);
    this._rig = null;
    this._scene.remove(this._floor);
    this._floor.geometry.dispose();
    this._floor.material.dispose();
    this._scene.remove(this._lines);
    this._lines.geometry.dispose();
    this._material.dispose();
    this._scene.remove(this._frame);
    this._frame.geometry.dispose();
    this._frameMaterial.dispose();
  }
}
